// src/Utils/recaptcha.ts
declare global {
    interface Window {
      grecaptcha?: {
        ready: (cb: () => void) => void;
        execute: (siteKey: string, opts: { action: string }) => Promise<string>;
      };
    }
  }
  
  const SITE_KEY = process.env.REACT_APP_RECAPTCHA_V3_SITE_KEY;
  
  let loadPromise: Promise<void> | null = null;
  
  function loadRecaptchaScriptOnce(timeoutMs = 12000): Promise<void> {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return Promise.reject(new Error('reCAPTCHA can only run in the browser.'));
    }
    if (!SITE_KEY) {
      return Promise.reject(
        new Error('Missing REACT_APP_RECAPTCHA_V3_SITE_KEY environment variable.')
      );
    }
    if (window.grecaptcha) return Promise.resolve();
  
    if (loadPromise) return loadPromise;
  
    loadPromise = new Promise<void>((resolve, reject) => {
      const existing = document.querySelector<HTMLScriptElement>(
        'script[data-recaptcha-v3="true"]'
      );
      if (existing) {
        // Script tag exists; wait for grecaptcha
        const started = Date.now();
        const tick = () => {
          if (window.grecaptcha) return resolve();
          if (Date.now() - started > timeoutMs) {
            return reject(new Error('Timed out waiting for reCAPTCHA to initialize.'));
          }
          window.setTimeout(tick, 50);
        };
        tick();
        return;
      }
  
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(
        SITE_KEY
      )}`;
      script.async = true;
      script.defer = true;
      script.setAttribute('data-recaptcha-v3', 'true');
  
      const timeoutId = window.setTimeout(() => {
        reject(new Error('Timed out loading reCAPTCHA script.'));
      }, timeoutMs);
  
      script.onload = () => {
        window.clearTimeout(timeoutId);
        if (!window.grecaptcha) {
          reject(new Error('reCAPTCHA script loaded but grecaptcha is unavailable.'));
          return;
        }
        resolve();
      };
  
      script.onerror = () => {
        window.clearTimeout(timeoutId);
        reject(new Error('Failed to load reCAPTCHA script.'));
      };
  
      document.head.appendChild(script);
    }).finally(() => {
      // allow retry if load failed
      // (keep promise if it succeeded; reset if it failed)
    });
  
    // Reset on failure to allow retry
    loadPromise.catch(() => {
      loadPromise = null;
    });
  
    return loadPromise;
  }
  
  export async function getToken(action: string): Promise<string> {
    if (!action?.trim()) throw new Error('reCAPTCHA action is required.');
    if (!SITE_KEY) {
      throw new Error('Missing REACT_APP_RECAPTCHA_V3_SITE_KEY environment variable.');
    }
  
    await loadRecaptchaScriptOnce();
  
    const grecaptcha = window.grecaptcha;
    if (!grecaptcha) throw new Error('reCAPTCHA is not available.');
  
    return await new Promise<string>((resolve, reject) => {
      grecaptcha.ready(() => {
        grecaptcha
          .execute(SITE_KEY, { action })
          .then((token) => {
            if (!token) throw new Error('Empty reCAPTCHA token received.');
            resolve(token);
          })
          .catch((err) => reject(err));
      });
    });
  }